/**
 * SheetViewer — просмотр xlsx/xls/ods/csv без OnlyOffice: файл читается в браузере
 * (SheetJS, ленивый чанк), листы — вкладками, ячейки — таблицей с объединениями.
 * Только просмотр; правка таблиц — через «Страницы» (таблицы по листам).
 */
import { useEffect, useMemo, useState } from "react";
import "@/styles/sheet-viewer.css";

interface SheetData {
  name: string;
  html: string;
  rows: number;
}

export function SheetViewer({ url, filename }: { url: string; filename: string }) {
  const [sheets, setSheets] = useState<SheetData[] | null>(null);
  const [active, setActive] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSheets(null);
    setError(null);
    (async () => {
      try {
        const [res, XLSX] = await Promise.all([fetch(url, { credentials: "include" }), import("xlsx")]);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;
        const wb = XLSX.read(buf, { type: "array", cellStyles: false, cellDates: true });
        const out: SheetData[] = wb.SheetNames.map((name) => {
          const ws = wb.Sheets[name];
          const ref = ws["!ref"];
          const rows = ref ? XLSX.utils.decode_range(ref).e.r + 1 : 0;
          // sheet_to_html учитывает объединённые ячейки и форматированные значения (w)
          const html = ref ? XLSX.utils.sheet_to_html(ws, { header: "", footer: "" }) : "";
          return { name, html, rows };
        });
        if (cancelled) return;
        setSheets(out);
        setActive(Math.max(0, out.findIndex((s) => s.rows > 0)));
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const current = useMemo(() => (sheets && sheets[active]) || null, [sheets, active]);

  if (error) {
    return (
      <div className="kbp-sheet-empty">
        Не удалось прочитать таблицу «{filename}»: {error}
      </div>
    );
  }
  if (!sheets) return <div className="kbp-sheet-empty">Читаю таблицу…</div>;
  if (sheets.length === 0) return <div className="kbp-sheet-empty">В файле нет листов</div>;

  return (
    <div className="kbp-sheet">
      {sheets.length > 1 && (
        <div className="kbp-sheet-tabs" role="tablist">
          {sheets.map((s, i) => (
            <button
              key={s.name}
              type="button"
              role="tab"
              aria-selected={i === active}
              className={"kbp-sheet-tab" + (i === active ? " is-active" : "")}
              onClick={() => setActive(i)}
              title={`${s.rows} строк`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="kbp-sheet-grid">
        {current && current.rows > 0 ? (
          // HTML от SheetJS: <table><tr><td colspan rowspan>…</td></tr></table>, без скриптов
          <div dangerouslySetInnerHTML={{ __html: current.html }} />
        ) : (
          <div className="kbp-sheet-empty">Лист пустой</div>
        )}
      </div>
    </div>
  );
}
